import React, { useState } from 'react';
import CalcShell from '../CalcShell';

/* Length units in meters — reused across all sections */
const LENGTH_UNITS = [
  { key: 'meters', label: 'meters' },
  { key: 'kilometers', label: 'kilometers' },
  { key: 'centimeters', label: 'centimeters' },
  { key: 'millimeters', label: 'millimeters' },
  { key: 'micrometers', label: 'micrometers' },
  { key: 'miles', label: 'miles' },
  { key: 'yards', label: 'yards' },
  { key: 'feet', label: 'feet' },
  { key: 'inches', label: 'inches' },
];

function UnitSelect({ value, onChange, testid }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
      className="px-3 py-2 rounded border border-slate-300 bg-white text-sm">
      {LENGTH_UNITS.map(u => <option key={u.key} value={u.key}>{u.label}</option>)}
    </select>
  );
}

function CalcButtons({ onCalc, onClear, testId }) {
  return (
    <div className="flex gap-2 mt-3">
      <button onClick={onCalc} data-testid={`${testId}-calc`}
        className="inline-flex items-center gap-2 px-5 py-2 rounded bg-[#4c7b25] hover:bg-[#3d621d] text-white font-semibold text-sm shadow-sm">
        <span className="inline-block w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent"></span> Calculate
      </button>
      <button onClick={onClear} data-testid={`${testId}-clear`}
        className="px-5 py-2 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium text-sm">
        Clear
      </button>
    </div>
  );
}

function InputRow({ label, value, onChange, unit, onUnitChange, testid, unitTestid }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_150px] items-center gap-2 mb-2">
      <label className="text-sm text-slate-700">{label}</label>
      <input type="number" step="any" value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="px-3 py-2 rounded border border-slate-300 bg-white w-full" />
      {onUnitChange && <UnitSelect value={unit} onChange={onUnitChange} testid={unitTestid} />}
    </div>
  );
}

/* Result block matching the Calculator.net "Result" bar and formula-substitution style */
function ResultBlock({ show, lines, finalValue, finalUnit }) {
  if (!show) return null;
  return (
    <div className="mt-3">
      <div className="bg-[#4c7b25] text-white px-4 py-1.5 text-lg font-bold rounded-t">Result</div>
      <div className="bg-white border border-slate-200 rounded-b p-4 font-serif">
        <div className="text-slate-800 text-[15px] leading-8">
          {lines.map((ln, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="min-w-[70px]">{i === 0 ? 'Volume =' : '='}</span>
              <span dangerouslySetInnerHTML={{ __html: ln }} />
            </div>
          ))}
          <div className="flex items-start gap-2 mt-1">
            <span className="min-w-[70px]">=</span>
            <span className="font-bold text-[#4c7b25] text-lg">
              {finalValue} <span className="text-base">{finalUnit}<sup>3</sup></span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShapeCard({ title, children }) {
  return (
    <section className="mt-6 first:mt-0">
      <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4 items-start">
        <div className="bg-slate-100 rounded-md p-4">
          {children[0]}
        </div>
        <div className="flex items-center justify-center p-3">
          {children[1]}
        </div>
      </div>
      {children[2]}
    </section>
  );
}

/* ============== DIAGRAMS ============== */
/* Green dashed lines and green labels matching Calculator.net */
const G = '#4a7c1f';
const dashed = { stroke: G, strokeDasharray: '3 3', fill: 'none' };
const solid = { stroke: '#0f172a', fill: 'none', strokeWidth: 1.5 };
const labelStyle = { fill: G, fontSize: 12, fontStyle: 'italic', fontFamily: 'serif' };

function SphereDiagram() {
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px]">
      <circle cx="100" cy="100" r="75" {...solid} />
      <ellipse cx="100" cy="100" rx="75" ry="18" {...dashed} />
      <line x1="100" y1="100" x2="170" y2="105" {...dashed} />
      <circle cx="100" cy="100" r="2" fill="#0f172a" />
      <text x="140" y="97" style={labelStyle}>r</text>
    </svg>
  );
}
function ConeDiagram() {
  return (
    <svg viewBox="0 0 200 220" className="w-full max-w-[220px]">
      <path d="M100,20 L165,170 L35,170" {...solid} />
      <ellipse cx="100" cy="170" rx="65" ry="15" {...solid} />
      <ellipse cx="100" cy="170" rx="65" ry="15" fill="none" stroke="#0f172a" strokeDasharray="3 3" opacity="0.5" />
      <line x1="100" y1="20" x2="100" y2="170" {...dashed} />
      <line x1="100" y1="170" x2="165" y2="175" {...dashed} />
      <circle cx="100" cy="170" r="2" fill="#0f172a" />
      <text x="105" y="100" style={labelStyle}>h</text>
      <text x="140" y="165" style={labelStyle}>r</text>
    </svg>
  );
}
function CubeDiagram() {
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px]">
      <rect x="40" y="60" width="100" height="100" {...solid} />
      <line x1="40" y1="60" x2="80" y2="30" {...solid} />
      <line x1="140" y1="60" x2="180" y2="30" {...solid} />
      <line x1="140" y1="160" x2="180" y2="130" {...solid} />
      <line x1="80" y1="30" x2="180" y2="30" {...solid} />
      <line x1="180" y1="30" x2="180" y2="130" {...solid} />
      <line x1="40" y1="160" x2="80" y2="130" {...dashed} strokeWidth="1" />
      <line x1="80" y1="30" x2="80" y2="130" {...dashed} strokeWidth="1" />
      <line x1="80" y1="130" x2="180" y2="130" {...dashed} strokeWidth="1" />
      <text x="140" y="175" style={labelStyle}>a</text>
    </svg>
  );
}
function CylinderDiagram() {
  return (
    <svg viewBox="0 0 200 220" className="w-full max-w-[220px]">
      <line x1="55" y1="40" x2="55" y2="180" {...solid} />
      <line x1="145" y1="40" x2="145" y2="180" {...solid} />
      <ellipse cx="100" cy="40" rx="45" ry="12" {...solid} />
      <path d="M55,180 A45,12 0 0 0 145,180" {...solid} />
      <path d="M55,180 A45,12 0 0 1 145,180" {...dashed} />
      <line x1="100" y1="40" x2="100" y2="180" {...dashed} />
      <line x1="100" y1="180" x2="145" y2="182" {...dashed} />
      <circle cx="100" cy="180" r="2" fill="#0f172a" />
      <text x="105" y="115" style={labelStyle}>h</text>
      <text x="122" y="178" style={labelStyle}>r</text>
    </svg>
  );
}
function RectTankDiagram() {
  return (
    <svg viewBox="0 0 240 200" className="w-full max-w-[240px]">
      <rect x="30" y="60" width="140" height="100" {...solid} />
      <line x1="30" y1="60" x2="70" y2="30" {...solid} />
      <line x1="170" y1="60" x2="210" y2="30" {...solid} />
      <line x1="170" y1="160" x2="210" y2="130" {...solid} />
      <line x1="70" y1="30" x2="210" y2="30" {...solid} />
      <line x1="210" y1="30" x2="210" y2="130" {...solid} />
      <line x1="30" y1="160" x2="70" y2="130" {...dashed} strokeWidth="1" />
      <line x1="70" y1="30" x2="70" y2="130" {...dashed} strokeWidth="1" />
      <line x1="70" y1="130" x2="210" y2="130" {...dashed} strokeWidth="1" />
      <text x="95" y="175" style={labelStyle}>l</text>
      <text x="180" y="175" style={labelStyle}>w</text>
      <text x="15" y="115" style={labelStyle}>h</text>
    </svg>
  );
}
function CapsuleDiagram() {
  return (
    <svg viewBox="0 0 260 160" className="w-full max-w-[240px]">
      <path d="M60,40 A40,40 0 0 0 60,120 L200,120 A40,40 0 0 0 200,40 Z" {...solid} />
      <ellipse cx="60" cy="80" rx="15" ry="40" {...dashed} />
      <ellipse cx="200" cy="80" rx="15" ry="40" {...dashed} />
      <line x1="60" y1="80" x2="200" y2="80" {...dashed} />
      <line x1="60" y1="80" x2="60" y2="120" {...dashed} />
      <text x="125" y="75" style={labelStyle}>h</text>
      <text x="65" y="105" style={labelStyle}>r</text>
    </svg>
  );
}
function EllipsoidDiagram() {
  return (
    <svg viewBox="0 0 220 200" className="w-full max-w-[220px]">
      <ellipse cx="110" cy="100" rx="80" ry="55" {...solid} />
      <ellipse cx="110" cy="100" rx="80" ry="15" {...dashed} />
      <line x1="110" y1="100" x2="190" y2="100" {...dashed} />
      <line x1="110" y1="100" x2="110" y2="45" {...dashed} />
      <line x1="110" y1="100" x2="60" y2="115" {...dashed} />
      <circle cx="110" cy="100" r="2" fill="#0f172a" />
      <text x="155" y="97" style={labelStyle}>a</text>
      <text x="115" y="65" style={labelStyle}>c</text>
      <text x="75" y="120" style={labelStyle}>b</text>
    </svg>
  );
}
function FrustumDiagram() {
  return (
    <svg viewBox="0 0 220 200" className="w-full max-w-[220px]">
      <path d="M70,40 L150,40 L190,170 L30,170 Z" {...solid} />
      <ellipse cx="110" cy="40" rx="40" ry="10" {...solid} />
      <path d="M30,170 A80,15 0 0 0 190,170" {...solid} />
      <path d="M30,170 A80,15 0 0 1 190,170" {...dashed} />
      <line x1="110" y1="40" x2="110" y2="170" {...dashed} />
      <line x1="110" y1="40" x2="150" y2="42" {...dashed} />
      <line x1="110" y1="170" x2="190" y2="172" {...dashed} />
      <text x="125" y="115" style={labelStyle}>h</text>
      <text x="127" y="35" style={labelStyle}>r</text>
      <text x="150" y="185" style={labelStyle}>R</text>
    </svg>
  );
}
function PyramidDiagram() {
  return (
    <svg viewBox="0 0 220 200" className="w-full max-w-[220px]">
      <path d="M110,20 L200,150 L20,150 Z" {...solid} />
      <path d="M20,150 L60,180 L160,180 L200,150" {...solid} />
      <line x1="60" y1="180" x2="160" y2="180" {...dashed} />
      <line x1="110" y1="20" x2="110" y2="165" {...dashed} />
      <line x1="20" y1="150" x2="160" y2="180" {...dashed} strokeWidth="1" />
      <text x="115" y="100" style={labelStyle}>h</text>
      <text x="105" y="195" style={labelStyle}>a</text>
    </svg>
  );
}
function SphericalCapDiagram() {
  return (
    <svg viewBox="0 0 240 200" className="w-full max-w-[240px]">
      <circle cx="120" cy="130" r="65" {...solid} />
      <path d="M60,110 A65,65 0 0 1 180,110" {...solid} strokeWidth="2.5" />
      <line x1="60" y1="110" x2="180" y2="110" {...dashed} />
      <line x1="120" y1="65" x2="120" y2="110" {...dashed} />
      <line x1="120" y1="130" x2="185" y2="132" {...dashed} />
      <line x1="120" y1="110" x2="180" y2="110" {...dashed} strokeWidth="1" />
      <circle cx="120" cy="130" r="2" fill="#0f172a" />
      <text x="125" y="90" style={labelStyle}>h</text>
      <text x="125" y="103" style={labelStyle}>r</text>
      <text x="155" y="127" style={labelStyle}>R</text>
    </svg>
  );
}
function TubeDiagram() {
  return (
    <svg viewBox="0 0 220 220" className="w-full max-w-[220px]">
      <line x1="55" y1="40" x2="55" y2="180" {...solid} />
      <line x1="165" y1="40" x2="165" y2="180" {...solid} />
      <line x1="80" y1="40" x2="80" y2="180" {...solid} />
      <line x1="140" y1="40" x2="140" y2="180" {...solid} />
      <ellipse cx="110" cy="40" rx="55" ry="12" {...solid} />
      <ellipse cx="110" cy="40" rx="30" ry="7" {...solid} />
      <path d="M55,180 A55,12 0 0 0 165,180" {...solid} />
      <path d="M80,180 A30,7 0 0 0 140,180" {...solid} />
      <path d="M55,180 A55,12 0 0 1 165,180" {...dashed} />
      <line x1="110" y1="40" x2="165" y2="42" {...dashed} />
      <line x1="110" y1="40" x2="140" y2="42" {...dashed} strokeWidth="1" />
      <line x1="55" y1="180" x2="55" y2="200" {...dashed} />
      <line x1="165" y1="180" x2="165" y2="200" {...dashed} />
      <text x="132" y="34" style={labelStyle}>d₁</text>
      <text x="115" y="34" style={labelStyle}>d₂</text>
      <text x="130" y="115" style={labelStyle}>l</text>
    </svg>
  );
}

/* ==================== SECTIONS ==================== */
function SphereSection() {
  const [r, setR] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const R = parseFloat(r);
    if (!R || R <= 0) return;
    const v = (4 / 3) * Math.PI * Math.pow(R, 3);
    setRes({ v, R });
  };
  return (
    <ShapeCard title="Sphere Volume Calculator">
      <div>
        <InputRow label="Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="sph-r" unitTestid="sph-unit" />
        <CalcButtons testId="sph-vol" onCalc={calc} onClear={() => { setR(''); setRes(null); }} />
      </div>
      <SphereDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(10)}
        lines={res ? [
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">4</span><span class="px-1">3</span></span>&nbsp;πr<sup>3</sup>`,
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">4</span><span class="px-1">3</span></span>&nbsp;×π×${res.R}<sup>3</sup>`,
        ] : []} />
    </ShapeCard>
  );
}

function ConeSection() {
  const [r, setR] = useState('');
  const [h, setH] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const R = parseFloat(r), H = parseFloat(h);
    if (!R || !H || R <= 0 || H <= 0) return;
    setRes({ v: (1 / 3) * Math.PI * R * R * H, R, H });
  };
  return (
    <ShapeCard title="Cone Volume Calculator">
      <div>
        <InputRow label="Base Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="cone-r" unitTestid="cone-r-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="cone-h" unitTestid="cone-h-unit" />
        <CalcButtons testId="cone-vol" onCalc={calc} onClear={() => { setR(''); setH(''); setRes(null); }} />
      </div>
      <ConeDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(10)}
        lines={res ? [
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">1</span><span class="px-1">3</span></span>&nbsp;πr<sup>2</sup>h`,
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">1</span><span class="px-1">3</span></span>&nbsp;×π×${res.R}<sup>2</sup>×${res.H}`,
        ] : []} />
    </ShapeCard>
  );
}

function CubeSection() {
  const [a, setA] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => { const A = parseFloat(a); if (!A || A <= 0) return; setRes({ v: A ** 3, A }); };
  return (
    <ShapeCard title="Cube Volume Calculator">
      <div>
        <InputRow label="Edge Length (a)" value={a} onChange={setA} unit={unit} onUnitChange={setUnit} testid="cube-a" unitTestid="cube-unit" />
        <CalcButtons testId="cube-vol" onCalc={calc} onClear={() => { setA(''); setRes(null); }} />
      </div>
      <CubeDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(10)}
        lines={res ? [`${res.A}<sup>3</sup>`] : []} />
    </ShapeCard>
  );
}

function CylinderSection() {
  const [r, setR] = useState('');
  const [h, setH] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const R = parseFloat(r), H = parseFloat(h);
    if (!R || !H || R <= 0 || H <= 0) return;
    setRes({ v: Math.PI * R * R * H, R, H });
  };
  return (
    <ShapeCard title="Cylinder Volume Calculator">
      <div>
        <InputRow label="Base Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="cyl-r" unitTestid="cyl-r-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="cyl-h" unitTestid="cyl-h-unit" />
        <CalcButtons testId="cyl-vol" onCalc={calc} onClear={() => { setR(''); setH(''); setRes(null); }} />
      </div>
      <CylinderDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(11)}
        lines={res ? [
          `πr<sup>2</sup>h`,
          `π×${res.R}<sup>2</sup>×${res.H}`,
          `${(res.R * res.R * res.H)}π`
        ] : []} />
    </ShapeCard>
  );
}

function RectTankSection() {
  const [l, setL] = useState(''), [w, setW] = useState(''), [h, setH] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const L = parseFloat(l), W = parseFloat(w), H = parseFloat(h);
    if (!L || !W || !H) return;
    setRes({ v: L * W * H, L, W, H });
  };
  return (
    <ShapeCard title="Rectangular Tank Volume Calculator">
      <div>
        <InputRow label="Length (l)" value={l} onChange={setL} unit={unit} onUnitChange={setUnit} testid="rect-l" unitTestid="rect-l-unit" />
        <InputRow label="Width (w)" value={w} onChange={setW} unit={unit} onUnitChange={setUnit} testid="rect-w" unitTestid="rect-w-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="rect-h" unitTestid="rect-h-unit" />
        <CalcButtons testId="rect-vol" onCalc={calc} onClear={() => { setL(''); setW(''); setH(''); setRes(null); }} />
      </div>
      <RectTankDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toString()}
        lines={res ? [`${res.L}×${res.W}×${res.H}`] : []} />
    </ShapeCard>
  );
}

function CapsuleSection() {
  const [r, setR] = useState(''), [h, setH] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const R = parseFloat(r), H = parseFloat(h);
    if (!R || !H || R <= 0 || H <= 0) return;
    setRes({ v: (4 / 3) * Math.PI * R ** 3 + Math.PI * R * R * H, R, H });
  };
  return (
    <ShapeCard title="Capsule Volume Calculator">
      <div>
        <InputRow label="Base Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="cap-r" unitTestid="cap-r-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="cap-h" unitTestid="cap-h-unit" />
        <CalcButtons testId="cap-vol" onCalc={calc} onClear={() => { setR(''); setH(''); setRes(null); }} />
      </div>
      <CapsuleDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(11)}
        lines={res ? [
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">4</span><span class="px-1">3</span></span>&nbsp;πr<sup>3</sup> + πr<sup>2</sup>h`,
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">4</span><span class="px-1">3</span></span>&nbsp;×π×${res.R}<sup>3</sup> + π×${res.R}<sup>2</sup>×${res.H}`
        ] : []} />
    </ShapeCard>
  );
}

function EllipsoidSection() {
  const [a, setA] = useState(''), [b, setB] = useState(''), [c, setC] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const A = parseFloat(a), B = parseFloat(b), C = parseFloat(c);
    if (!A || !B || !C) return;
    setRes({ v: (4 / 3) * Math.PI * A * B * C, A, B, C });
  };
  return (
    <ShapeCard title="Ellipsoid Volume Calculator">
      <div>
        <InputRow label="Axis 1 (a)" value={a} onChange={setA} unit={unit} onUnitChange={setUnit} testid="ell-a" unitTestid="ell-a-unit" />
        <InputRow label="Axis 2 (b)" value={b} onChange={setB} unit={unit} onUnitChange={setUnit} testid="ell-b" unitTestid="ell-b-unit" />
        <InputRow label="Axis 3 (c)" value={c} onChange={setC} unit={unit} onUnitChange={setUnit} testid="ell-c" unitTestid="ell-c-unit" />
        <CalcButtons testId="ell-vol" onCalc={calc} onClear={() => { setA(''); setB(''); setC(''); setRes(null); }} />
      </div>
      <EllipsoidDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(10)}
        lines={res ? [
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">4</span><span class="px-1">3</span></span>&nbsp;πabc`,
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">4</span><span class="px-1">3</span></span>&nbsp;×π×${res.A}×${res.B}×${res.C}`
        ] : []} />
    </ShapeCard>
  );
}

function ConicalFrustumSection() {
  const [r, setR] = useState(''), [R2, setR2] = useState(''), [h, setH] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const r1 = parseFloat(r), r2 = parseFloat(R2), H = parseFloat(h);
    if (!r1 || !r2 || !H) return;
    setRes({ v: (1 / 3) * Math.PI * H * (r1 * r1 + r1 * r2 + r2 * r2), r1, r2, H });
  };
  return (
    <ShapeCard title="Conical Frustum Volume Calculator">
      <div>
        <InputRow label="Top Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="fru-r" unitTestid="fru-r-unit" />
        <InputRow label="Bottom Radius (R)" value={R2} onChange={setR2} unit={unit} onUnitChange={setUnit} testid="fru-R" unitTestid="fru-R-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="fru-h" unitTestid="fru-h-unit" />
        <CalcButtons testId="fru-vol" onCalc={calc} onClear={() => { setR(''); setR2(''); setH(''); setRes(null); }} />
      </div>
      <FrustumDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(11)}
        lines={res ? [
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">1</span><span class="px-1">3</span></span>&nbsp;πh(r<sup>2</sup> + rR + R<sup>2</sup>)`,
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">1</span><span class="px-1">3</span></span>&nbsp;×π×${res.H}(${res.r1}<sup>2</sup> + ${res.r1}×${res.r2} + ${res.r2}<sup>2</sup>)`
        ] : []} />
    </ShapeCard>
  );
}

function PyramidSection() {
  const [a, setA] = useState(''), [h, setH] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const A = parseFloat(a), H = parseFloat(h);
    if (!A || !H) return;
    setRes({ v: (1 / 3) * A * A * H, A, H });
  };
  return (
    <ShapeCard title="Square Pyramid Volume Calculator">
      <div>
        <InputRow label="Base Edge (a)" value={a} onChange={setA} unit={unit} onUnitChange={setUnit} testid="pyr-a" unitTestid="pyr-a-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="pyr-h" unitTestid="pyr-h-unit" />
        <CalcButtons testId="pyr-vol" onCalc={calc} onClear={() => { setA(''); setH(''); setRes(null); }} />
      </div>
      <PyramidDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(12)}
        lines={res ? [
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">1</span><span class="px-1">3</span></span>&nbsp;a<sup>2</sup>h`,
          `<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">1</span><span class="px-1">3</span></span>&nbsp;×${res.A}<sup>2</sup>×${res.H}`
        ] : []} />
    </ShapeCard>
  );
}

function SphericalCapSection() {
  const [R, setR] = useState(''), [r1, setR1] = useState(''), [h, setH] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    // Accepts any 2 of R, r1, h
    const values = { R: parseFloat(R), r1: parseFloat(r1), h: parseFloat(h) };
    let { R: Rv, r1: rv, h: hv } = values;
    // Formulas: h = R - sqrt(R^2 - r1^2), r1 = sqrt(2Rh - h^2), R = (r1^2 + h^2)/(2h)
    const has = { R: !!Rv, r1: !!rv, h: !!hv };
    const provided = Object.values(has).filter(Boolean).length;
    if (provided < 2) return;
    if (!has.h) hv = Rv - Math.sqrt(Rv * Rv - rv * rv);
    else if (!has.r1) rv = Math.sqrt(2 * Rv * hv - hv * hv);
    else if (!has.R) Rv = (rv * rv + hv * hv) / (2 * hv);
    const v = (1 / 3) * Math.PI * hv * hv * (3 * Rv - hv);
    const hemisphereNote = Math.abs(Rv - rv) < 1e-9;
    setRes({ v, R: Rv, r1: rv, h: hv, hemisphereNote });
  };
  return (
    <ShapeCard title="Spherical Cap Volume Calculator">
      <div>
        <div className="text-xs text-slate-600 mb-2">Please provide any 2 values.</div>
        <InputRow label="Ball Radius (R)" value={R} onChange={setR} unit={unit} onUnitChange={setUnit} testid="cap-R" unitTestid="cap-R-unit" />
        <InputRow label="Base Radius (r)" value={r1} onChange={setR1} unit={unit} onUnitChange={setUnit} testid="cap-r1" unitTestid="cap-r1-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="cap-h" unitTestid="cap-h-unit" />
        <CalcButtons testId="spcap-vol" onCalc={calc} onClear={() => { setR(''); setR1(''); setH(''); setRes(null); }} />
      </div>
      <SphericalCapDiagram />
      {res && (
        <div className="mt-3">
          <div className="bg-[#4c7b25] text-white px-4 py-1.5 text-lg font-bold rounded-t">Result</div>
          <div className="bg-white border border-slate-200 rounded-b p-4 text-[15px] leading-8 font-serif">
            <div className="text-slate-700 mb-1">The calculator only needs two values. The following result is based on the two you provided.</div>
            {res.hemisphereNote && <div className="text-slate-700 mb-1">It is a hemisphere since R = r.</div>}
            <div>Height = <b>{res.h.toFixed(4)}</b> {unit}</div>
            <div>Ball Radius (R) = <b>{res.R.toFixed(4)}</b> {unit}</div>
            <div>Base Radius (r) = <b>{res.r1.toFixed(4)}</b> {unit}</div>
            <div className="flex gap-2 mt-1">
              <span>Volume = </span>
              <span><span className="inline-flex flex-col items-center align-middle mx-0.5"><span className="border-b border-slate-800 px-1">1</span><span className="px-1">3</span></span> πh<sup>2</sup>(3R − h)</span>
            </div>
            <div className="flex gap-2">
              <span>=</span>
              <span><span className="inline-flex flex-col items-center align-middle mx-0.5"><span className="border-b border-slate-800 px-1">1</span><span className="px-1">3</span></span> ×π×{res.h.toFixed(2)}<sup>2</sup>×(3×{res.R.toFixed(2)} − {res.h.toFixed(2)})</span>
            </div>
            <div className="mt-1"><span className="font-bold text-[#4c7b25] text-lg">= {res.v.toFixed(11)} {unit}<sup>3</sup></span></div>
          </div>
        </div>
      )}
    </ShapeCard>
  );
}

function TubeSection() {
  const [d1, setD1] = useState(''), [d2, setD2] = useState(''), [l, setL] = useState('');
  const [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const D1 = parseFloat(d1), D2 = parseFloat(d2), L = parseFloat(l);
    if (!D1 || !D2 || !L || D1 <= D2) return;
    setRes({ v: Math.PI * ((D1 * D1 - D2 * D2) / 4) * L, D1, D2, L });
  };
  return (
    <ShapeCard title="Tube Volume Calculator">
      <div>
        <InputRow label="Outer Diameter (d₁)" value={d1} onChange={setD1} unit={unit} onUnitChange={setUnit} testid="tube-d1" unitTestid="tube-d1-unit" />
        <InputRow label="Inner Diameter (d₂)" value={d2} onChange={setD2} unit={unit} onUnitChange={setUnit} testid="tube-d2" unitTestid="tube-d2-unit" />
        <InputRow label="Length (l)" value={l} onChange={setL} unit={unit} onUnitChange={setUnit} testid="tube-l" unitTestid="tube-l-unit" />
        <CalcButtons testId="tube-vol" onCalc={calc} onClear={() => { setD1(''); setD2(''); setL(''); setRes(null); }} />
      </div>
      <TubeDiagram />
      <ResultBlock show={!!res} finalUnit={unit}
        finalValue={res && res.v.toFixed(12)}
        lines={res ? [
          `π<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">d₁<sup>2</sup> − d₂<sup>2</sup></span><span class="px-1">4</span></span>l`,
          `π×<span class="inline-flex flex-col items-center align-middle mx-0.5"><span class="border-b border-slate-800 px-1">${res.D1}<sup>2</sup> − ${res.D2}<sup>2</sup></span><span class="px-1">4</span></span>×${res.L}`,
          `${((res.D1 * res.D1 - res.D2 * res.D2) / 4 * res.L)}π`
        ] : []} />
    </ShapeCard>
  );
}

export default function VolumeCalc() {
  return (
    <CalcShell look="cnet"
      title="Volume Calculator"
      subtitle="The following is a list of volume calculators for several common shapes. Please fill in the corresponding fields and click the &quot;Calculate&quot; button."
    >
      <SphereSection />
      <ConeSection />
      <CubeSection />
      <CylinderSection />
      <RectTankSection />
      <CapsuleSection />
      <SphericalCapSection />
      <ConicalFrustumSection />
      <EllipsoidSection />
      <PyramidSection />
      <TubeSection />
    </CalcShell>
  );
}
